import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Text,
  Input,
  useToast
} from '@chakra-ui/react';
import Button from 'components/atoms/Button/Button';
import ProductImages from '../ProductImages/ProductImages';
import {
  ProductImageContainer,
  ProductGiftContainer,
  AddToCartContainer
} from './ProductDetails.styled';
import ProductOptions from '../ProductOptions/ProductOptions';
import BackInStock from '../BackInStock/BackInStock';
import AddToCartButton from '../AddToCartButton/AddToCartButton';
import { isEqual } from 'lodash';

const ProductGiftPopup = ({ giftModal = false, setGiftModal, giftProduct }) => {
  const toast = useToast();
  const [activeImg, setActiveImg] = useState(0);
  const [modifiersValue, setModifiersValue] = useState({});
  const [modifierError, setModifierError] = useState([]);
  const [variantSpecs, setVariantSpecs] = useState([]);
  const [activeVariant, setActiveVariant] = useState(0);

  const sliderGiftRef = useRef();

  const sortedImages = useMemo(() => {
    if (giftProduct?.images) {
      const imagesObj = [...giftProduct.images];
      imagesObj.sort((a, b) => (a.is_thumbnail ? -1 : b.is_thumbnail ? 1 : 0));
      return imagesObj;
    } else {
      return [];
    }
  }, [giftProduct]);

  useEffect(() => {
    // if (
    //   giftProduct &&
    //   typeof giftProduct.inventory_tracking !== 'undefined' &&
    //   typeof giftProduct.inventory_level !== 'undefined' &&
    //   typeof giftProduct.inventory_warning_level !== 'undefined' &&
    //   giftProduct.inventory_tracking === 'product' &&
    //   giftProduct.inventory_level < 1
    // ) {
    //   setQuantity(0);
    // }

    // Set active variant
    if (giftProduct && giftProduct.variants?.length > 0 && !activeVariant) {
      let variantInStock = giftProduct.variants[0];

      if (giftProduct.inventory_tracking !== 'none') {
        const findVariantInStock = giftProduct.variants.find(
          variant => variant.inventory_level > 0
        );
        if (findVariantInStock) variantInStock = findVariantInStock;
      }

      setActiveVariant(variantInStock?.entityId || variantInStock?.id);
      setVariantSpecs(variantInStock?.option_values);

      // setStockObj({ ...variants[0], availability: product.availability});
    } else if (giftProduct && giftProduct.variants?.length === 0 && !activeVariant) {
      setActiveVariant(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftProduct, activeVariant]);

  useEffect(() => {
    if (giftProduct) {
      const tempActiveVariant =
        giftProduct.variants && Array.isArray(giftProduct.variants)
          ? giftProduct.variants.filter(variant => isEqual(variant.option_values, variantSpecs))
          : [];

      if (tempActiveVariant !== undefined && tempActiveVariant?.length !== 0) {
        let assignVariant = tempActiveVariant[0];
        setActiveVariant(assignVariant?.entityId || assignVariant?.id);
      }
    }
  }, [variantSpecs, giftProduct]);

  const modifiersDisplay = useMemo(() => {
    return giftProduct?.modifiers?.filter(
      i =>
        ![
          'Ships Every',
          'Upload Prescription',
          'Prescription Status',
          'Cold Shipping Terms & Conditions'
        ].some(term => i.name.includes(term))
    );
  }, [giftProduct]);

  const productOptionsSorted = giftProduct?.options?.map(item => ({
    ...item,
    option_values: [...item.option_values].sort((a, b) => a.sort_order - b.sort_order)
  }));

  const isPc = window.innerWidth >= 1024;

  const selectedVariant = useMemo(() => {
    let findVariant = undefined;

    if (activeVariant && typeof activeVariant === 'number') {
      findVariant = giftProduct.variants?.find(variant => variant.id === activeVariant);
    }
    return findVariant;
  }, [activeVariant, giftProduct]);

  const options = useMemo(() => {
    const collection = [];

    if (modifiersDisplay && modifiersDisplay.length > 0) {
      modifiersDisplay.forEach(m =>
        collection.push({
          option_id: m?.id,
          option_value: modifiersValue[m?.name] || ''
        })
      );
      if (selectedVariant?.option_values?.length > 0) {
        collection.push({
          option_id: selectedVariant?.option_values?.[0]?.option_id,
          option_value: selectedVariant?.option_values?.[0]?.id
        });
      }
    }
    return collection;
  }, [modifiersDisplay, modifiersValue, selectedVariant]);

  const isOutOfStock =
    giftProduct &&
    giftProduct.inventory_tracking !== 'none' &&
    (giftProduct.inventory_level === 0 || selectedVariant?.inventory_level === 0);
//   console.log('isOutOfStock', selectedVariant);
  const handleVariantChange = (id, tempVariant) => {
    const newVariantSpecs = variantSpecs.map(variant => {
      if (id === variant.option_id) {
        return {
          id: tempVariant.id,
          label: tempVariant.label,
          option_display_name: variant.option_display_name,
          option_id: variant.option_id
        };
      } else {
        return variant;
      }
    });
    setVariantSpecs(newVariantSpecs);
  };

  const handleAfterAddToCart = () => {
    toast({
      position: 'top-right',
      title: 'Added to cart',
      colorScheme: 'primary',
      description: 'Item has been added to your cart.',
      status: 'success'
    });
    setGiftModal(false);
    setActiveImg(0);
  };

  const onModifiersChange = (value, name) => {
    const currentModifiers = { ...modifiersValue };
    currentModifiers[name] = value;
    setModifiersValue(currentModifiers);
  };
  if (!giftProduct) {
    return <></>;
  }
  return (
    <Modal isOpen={giftModal} onClose={() => setGiftModal(false)} isCentered>
      <ModalOverlay />
      <ModalContent
        px={{ base: '24px', lg: '64px' }}
        pt={{ base: '40px', lg: '36px' }}
        pb="40px"
        maxW={'687px'}
        borderRadius={{ base: 0, lg: '12px' }}
      >
        <ModalCloseButton top={'16px'} right={'16px'} />
        <ModalBody p="0">
          <Text fontSize={24} mb={2}>
            You have received a gift!
          </Text>
          <ProductGiftContainer>
            <div className="productTopEntry">
              <ProductImageContainer>
                <div className="productTopCol colThumbnails">
                  <ProductImages
                    ref={sliderGiftRef}
                    product={giftProduct}
                    activeIndex={activeImg}
                    setActiveIndex={setActiveImg}
                    sortedImages={sortedImages}
                    identifier="gift-images"
                    type="thumbnails"
                  />
                </div>
                <div className="productTopCol colImages">
                  <ProductImages
                    ref={sliderGiftRef}
                    product={giftProduct}
                    activeIndex={activeImg}
                    setActiveIndex={setActiveImg}
                    sortedImages={sortedImages}
                    identifier="gift-images"
                    type="gallery"
                  />
                </div>
              </ProductImageContainer>
              <div className={`productTopCol colDetails`}>
                <div className={'productTitle'}>
                  <h3 className="h3">{giftProduct && giftProduct.name}</h3>
                </div>
                {modifiersDisplay &&
                  modifiersDisplay
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((m, index) => (
                      <div key={`modifiers-` + index}>
                        <Text className={'label'}>
                          {m.display_name.includes("Pet's Name") ? "Pet's Name" : m.display_name}
                        </Text>
                        <Input
                          placeholder={m.display_name}
                          isInvalid={modifierError.find(error => error.name === m.name)?.required}
                          value={modifiersValue[m.name]}
                          onChange={e => {
                            const currentItem = modifierError.find(error => error.name === m.name);
                            if (currentItem) {
                              const newModifierError = modifierError.filter(
                                item => item.name !== currentItem.name
                              );
                              setModifierError(newModifierError);
                            }
                            onModifiersChange(e.target.value, m.name);
                          }}
                          mb={4}
                          bg={'white'}
                          fontSize={isPc ? 'sm' : 16}
                        />
                      </div>
                    ))}

                {productOptionsSorted && productOptionsSorted?.length > 0 && (
                  <ProductOptions
                    options={productOptionsSorted}
                    variantSpecs={variantSpecs}
                    variants={giftProduct.variants}
                    track={giftProduct.inventory_tracking !== 'none'}
                    handleVariantChange={handleVariantChange}
                  />
                )}
                {isOutOfStock && (
                  <BackInStock
                    product={giftProduct}
                    variant={activeVariant}
                    className={'outOfStock'}
                  />
                )}
                {giftProduct.availability === 'disabled' ? (
                  <AddToCartContainer>
                    <Button
                      level="primary"
                      size="fullWidth"
                      href="https://petchemist.brainfish.ai/"
                    >
                      Contact us
                    </Button>
                  </AddToCartContainer>
                ) : (
                  <>
                    <AddToCartContainer>
                      <AddToCartButton
                        fullWidth
                        product={giftProduct}
                        // needModifiers={onValidateModifiers}
                        options={options}
                        tracker={handleAfterAddToCart}
                        selectedVariant={selectedVariant}
                        track={giftProduct.inventory_tracking !== 'none'}
                        disabled={isOutOfStock}
                        productId={giftProduct.entityId || giftProduct.id}
                        variantId={activeVariant}
                        // intercept={!!notice}
                        quantity={1}
                        isGift={true}
                        // extraItems={warranty ? [{ productId: warranty, matchQty: true }] : []}
                        // giftProductId={giftProductId}
                      >
                        {isOutOfStock ? 'Out of Stock' : 'Add to cart'}
                      </AddToCartButton>
                    </AddToCartContainer>
                  </>
                )}
              </div>
            </div>
          </ProductGiftContainer>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
export default ProductGiftPopup;
